import { Link } from "react-router-dom";
import type { Anime } from "@/types";
import { useFavorites } from "@/hooks/useFavorites";

interface AnimeCardProps {
  anime: Anime;
  showFavorite?: boolean;
}

export default function AnimeCard({ anime, showFavorite = true }: AnimeCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(anime.id);

  return (
    <Link to={`/detail/${anime.id}`} className="group block">
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-background-200">
        <img
          src={anime.cover}
          alt={`${anime.title} 动漫封面`}
          title={`${anime.title} - 在线观看与详情介绍`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-foreground-950/80 to-transparent" />
        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-foreground-950/60 px-1.5 py-0.5 font-label text-[11px] text-background-50 backdrop-blur-sm">
          <i className="ri-star-fill text-primary-300" />
          {anime.rating.toFixed(1)}
        </span>
        {showFavorite && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              toggleFavorite(anime.id);
            }}
            aria-label={favorited ? "取消收藏" : "收藏该动漫"}
            className={[
              "absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md backdrop-blur-sm transition-colors",
              favorited
                ? "bg-primary-500 text-background-50"
                : "bg-foreground-950/40 text-background-50 hover:bg-primary-500",
            ].join(" ")}
          >
            <i className={favorited ? "ri-heart-3-fill text-sm" : "ri-heart-3-line text-sm"} />
          </button>
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-2.5 pb-2">
          <span className="rounded bg-background-50/90 px-1.5 py-0.5 font-label text-[10px] text-foreground-800">
            {anime.status}
          </span>
          <span className="font-label text-[11px] text-background-50">{anime.updateText}</span>
        </div>
      </div>
      <h3 className="mt-2.5 line-clamp-1 text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600">
        {anime.title}
      </h3>
      <p className="mt-1 line-clamp-1 text-xs text-foreground-500">
        {anime.year} · {anime.region} · {anime.genres.join(" / ")}
      </p>
    </Link>
  );
}